// The notes and summaries a settings section opens or closes with (spec/13 §13, 2026-10-07) — words that explain how
// a section's settings work together, and the summary of a thing whose home is another screen.
//
//   PaygSummary      Time & pricing → Pay-as-you-go: one home per thing, so the price, step, sign-in allowance and
//                    wallets paying for play are edited in Pricing → Pay as you go; here they are one summary row
//   LimitsNote       Staff & limits: the lower of this and the role's own limit applies, with Staff → Roles
//   AgeRulesNote     Players & sign-in: the three age rules read together, with Stations → Classes
//   OfflinePlayNote  Sessions & PCs → Offline play: what each option does and how they combine
//   DaysNote         Cash & shifts → Business day: the two ways a day ends, and the venue's cutoff
//   CleanupOffNote   Sessions & PCs → Between players: "not recommended" while clearing is Off
//   OnlinePaymentsStatusLine  Money → Payments: whether the platform has the venue's organisation set up to take online payments

import type { SettingEntry, SettingsView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { DaysAndShiftsHelp } from '../org/shifts/days-and-shifts';
import { useBusinessDayStatus } from '../org/shifts/queries';
import { useOnlinePayments } from '../org/payments/queries';
import { useCurrentVenue } from '../shell/scope';
import { Notice } from '../ui';
import type { SettingsScope } from './registry-source';

/** Where a screen's other pages are: the Pricing tab, Staff → Roles, Stations → Classes, the venue profile. */
export interface ScreenLinks {
  readonly pricing?: { readonly to: string; readonly params: Readonly<Record<string, string>> };
  readonly roles?: { readonly to: string; readonly params: Readonly<Record<string, string>> };
  readonly classes?: { readonly to: string; readonly params: Readonly<Record<string, string>> };
  readonly venueProfile?: { readonly to: string; readonly params: Readonly<Record<string, string>> };
}

const entryOf = (view: SettingsView, key: string): SettingEntry | undefined => view.settings.find((entry) => entry.key === key);

/** Pay-as-you-go in one line, with the way to where it is set. */
export function PaygSummary({ view, link }: { readonly view: SettingsView; readonly link: ScreenLinks['pricing'] }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const enabled = entryOf(view, 'sessions.payAsYouGoEnabled');
  const rate = entryOf(view, 'sessions.paygHourlyRate');
  const step = entryOf(view, 'sessions.paygBillingUnitSeconds');
  if (enabled === undefined && rate === undefined) return null;
  const currency = view.scope.currency;
  const on = enabled?.value !== false;
  const stepText = t('settings.unit.minutes', { count: Math.round((typeof step?.value === 'number' ? step.value : 60) / 60) });
  const summary = !on
    ? t('settings.payg.summaryOff')
    : typeof rate?.value === 'number' && rate.value > 0
      ? t('settings.payg.summaryOn', { rate: format.money({ amount: rate.value, currency }), step: stepText })
      : t('settings.payg.summaryNoRate');
  return (
    <section className="console-section console-payg-summary" aria-labelledby="console-payg-summary-title" data-setting="panel.payg">
      <header className="console-section__head">
        <div className="console-section__heading">
          <h3 id="console-payg-summary-title" className="console-section__title">
            {t('settings.section.payg')}
          </h3>
          <p className="console-section__description">{summary}</p>
        </div>
        {link === undefined ? null : (
          <div className="console-section__actions">
            <Link className="clav-button clav-button--secondary clav-button--sm" to={link.to} params={link.params}>
              {t('settings.payg.open')}
            </Link>
          </div>
        )}
      </header>
      <div className="console-section__body">
        <p className="console-muted">{t('settings.payg.where')}</p>
      </div>
    </section>
  );
}

export function LimitsNote({ link }: { readonly link: ScreenLinks['roles'] }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Notice tone="info" role="note" title={t('settings.limits.title')}>
      <p>{t('settings.limits.body')}</p>
      {link === undefined ? null : (
        <p>
          <Link to={link.to} params={link.params}>
            {t('settings.limits.link')}
          </Link>
        </p>
      )}
    </Notice>
  );
}

export function AgeRulesNote({ link }: { readonly link: ScreenLinks['classes'] }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Notice tone="info" role="note" title={t('settings.ageRules.title')}>
      <ul>
        <li>{t('settings.ageRules.minAge')}</li>
        <li>
          {t('settings.ageRules.classAge')}{' '}
          {link === undefined ? null : (
            <Link to={link.to} params={link.params}>
              {t('settings.ageRules.classLink')}
            </Link>
          )}
        </li>
        <li>{t('settings.ageRules.verified')}</li>
      </ul>
    </Notice>
  );
}

/**
 * What each Offline play option does, and how they combine (spec/05 §8.1; the owner's decision of 2026-09-30): four
 * switches that interact read better with their rules in words.
 */
export function OfflinePlayNote(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="settings-offline-intro">
      <Notice tone="info" role="note" title={t('settings.offlinePlay.title')}>
        <ul>
          <li>{t('settings.offlinePlay.keepRunning')}</li>
          <li>{t('settings.offlinePlay.staffCodes')}</li>
          <li>{t('settings.offlinePlay.knownPlayers')}</li>
          <li>{t('settings.offlinePlay.strictLock')}</li>
        </ul>
      </Notice>
    </div>
  );
}

/**
 * Days and shifts (ADR-0029): the two ways a business day ends and whom each suits — automatic for a venue open 24×7 —
 * and, on a venue's screen, the cutoff the day runs between (the Venue profile's, never a second copy here), with the
 * "How days and shifts work" explainer one click away.
 */
export function DaysNote({ scope, venueProfile }: { readonly scope: SettingsScope; readonly venueProfile: ScreenLinks['venueProfile'] }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="settings-offline-intro">
      <Notice tone="info" role="note" title={t('settings.daysAndShifts.title')}>
        <ul>
          <li>{t('settings.daysAndShifts.manual')}</li>
          <li>{t('settings.daysAndShifts.auto')}</li>
        </ul>
        <p className="settings-days-subtitle">{t('settings.daysAndShifts.peopleTitle')}</p>
        <ul>
          <li>{t('settings.daysAndShifts.perDrawer')}</li>
          <li>{t('settings.daysAndShifts.sharedDrawer')}</li>
          <li>{t('settings.daysAndShifts.floorWithoutDrawer')}</li>
        </ul>
      </Notice>
      {scope.kind === 'venue' && venueProfile !== undefined ? (
        <VenueDayCutoff organizationId={scope.organizationId} venueId={scope.venueId} venueProfile={venueProfile} />
      ) : null}
    </div>
  );
}

/** The venue's cutoff, where it is set, and the explainer — on a venue's screen only, where the venue is known. */
function VenueDayCutoff({
  organizationId,
  venueId,
  venueProfile,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueProfile: NonNullable<ScreenLinks['venueProfile']>;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const status = useBusinessDayStatus(organizationId, venueId);
  const [helping, setHelping] = useState(false);
  if (status.data === undefined) return null;
  const { cutoff, dayCloseMode, cashHandling } = status.data;
  return (
    <div className="settings-day-cutoff">
      {t('settings.daysAndShifts.cutoff', { cutoff })}{' '}
      <Link to={venueProfile.to} params={venueProfile.params}>
        {t('settings.daysAndShifts.cutoffLink')}
      </Link>{' '}
      <Button variant="ghost" size="sm" onClick={() => setHelping(true)}>
        {t('settings.daysAndShifts.help')}
      </Button>
      <DaysAndShiftsHelp open={helping} onOpenChange={setHelping} status={{ cutoff, dayCloseMode, venueId, cashHandling }} venueSlug={venue.slug} />
    </div>
  );
}

/**
 * The "not recommended" callout for `cleanup.profile = off` (F-LCK-07, spec/11 §5): off clears nothing beyond the
 * kiosk's own data between players, so the next player can reach the one before's browser and launcher sign-ins
 * unless the venue resets the disk another way. Shown only while Off is the value in force on the screen.
 */
export function CleanupOffNote({ profile }: { readonly profile: unknown }): ReactNode {
  const { t } = useTranslation('console');
  if (profile !== 'off') return null;
  return (
    <Notice tone="warn" role="note" title={t('settings.stationsAndLocker.cleanupOffTitle')}>
      {t('settings.stationsAndLocker.cleanupOffWarning')}
    </Notice>
  );
}

const ONLINE_TONE = {
  active: 'ok',
  pending: 'info',
  action_required: 'warn',
  not_onboarded: 'info',
  rejected: 'danger',
  suspended: 'danger',
} as const;

/**
 * Whether online payments can work here, read only: the organisation is set up to receive its share by head office,
 * not by the venue. Nothing shows while the status is unknown, or when the payment provider takes everything itself
 * (no payee, so `payeeStatus` is null).
 */
export function OnlinePaymentsStatusLine({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const status = useOnlinePayments(organizationId, venueId).data;
  if (status === undefined) return null;
  if (status.provider === null) {
    return (
      <Notice tone="info" role="note">
        <p>{t('settings.onlineStatus.noProvider')}</p>
      </Notice>
    );
  }
  if (status.payeeStatus === null || status.payeeStatus === undefined) return null;
  return (
    <Notice tone={ONLINE_TONE[status.payeeStatus]} role="note">
      <p>{t(`settings.onlineStatus.${status.payeeStatus}`)}</p>
    </Notice>
  );
}
